import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useFlexLayout, useRowSelect, useSortBy, useTable } from 'react-table';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';

import { getProductGridColumns } from './products/product-grid-columns';
import { openProductDetailModal } from '../../../../../store/products/products.actions';
import { useEnableUnitsOfMeasure } from '../../../../../feature-flags/hooks/use-enable-units-of-measure';
import PurchaseOrderProgressBar from '../purchase-order-progress-bar';
import { messages } from './ordered-products.messages';
import styles from './ordered-products.styles.module.less';

/**
 * Ordered Products component
 * Displays the ordered products table for a purchase order
 * Reuses the ProductGrid component structure but with columns specific to the order preview
 * @typedef {object} Product
 * @property {number} [productId]
 * @property {string} [sku]
 * @property {number} [quantity]
 * @property {number} [cost]
 * @property {number} [taxAmount]
 * @property {string} [supplierSku]
 * @property {string} [noteToSupplier]
 * @property {number} [totalOrdered]
 * @property {number} [totalReceived]
 * @property {number} [totalRejected]
 */

/**
 *  Ordered Products component
 *  Displays products connected to purchase order
 *  @param { object } props
 *  @param { Product[] } props.products
 *  @param { string } props.currencyCode
 *  @returns {JSX.Element}
 */
export const OrderedProducts = ({ products, currencyCode }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const isUnitsOfMeasureEnabled = useEnableUnitsOfMeasure();

  const totalOrdered = products.reduce((sum, product) => sum + (product.totalOrdered || 0), 0);
  const totalReceived = products.reduce((sum, product) => sum + (product.totalReceived || 0), 0);
  const totalRejected = products.reduce((sum, product) => sum + (product.totalRejected || 0), 0);

  const openProductDetails = useCallback(
    (productId) => {
      if (!productId) return;
      dispatch(openProductDetailModal(productId));
    },
    [dispatch],
  );

  const columns = useMemo(
    () => getProductGridColumns(openProductDetails, currencyCode, isUnitsOfMeasureEnabled),
    [openProductDetails, currencyCode, isUnitsOfMeasureEnabled],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: products,
    },
    useSortBy,
    useFlexLayout,
    useRowSelect,
  );

  return (
    <div className={styles.container}>
      <Text className={styles['section-title']} textType={TextType.H4} value={intl.formatMessage(messages.orderedProducts)} />
      <PurchaseOrderProgressBar receivedItems={totalReceived} rejectedItems={totalRejected} showDescription totalItems={totalOrdered} />

      <div className={styles['table-container']}>
        <ReactTableWrapper
          className={styles.table}
          emptyRow={
            <Text
              className={styles['empty-row-text']}
              textType={TextType.Paragraph}
              value={intl.formatMessage(messages.noProductsPlaceholder)}
            />
          }
          getTableBodyProps={getTableBodyProps}
          getTableProps={getTableProps}
          headerGroups={headerGroups}
          prepareRow={prepareRow}
          rows={rows}
        />
      </div>

      {products.length > 0 && (
        <Text
          textType={TextType.Paragraph}
          value={intl.formatMessage(messages.summary, {
            total: products.length,
          })}
        />
      )}
    </div>
  );
};
